/**
 * Redirect URL validation utilities
 *
 * Prevents open redirect vulnerabilities by ensuring redirect URLs
 * are relative paths within the application.
 */

/**
 * Validates that a redirect URL is a safe relative path.
 *
 * Security considerations:
 * - Must start with / (relative path)
 * - Cannot be protocol-relative (//evil.com)
 * - Cannot contain javascript: URLs
 * - Cannot use encoded characters to bypass checks
 *
 * @param url - The URL to validate
 * @returns The validated URL if safe, null otherwise
 */
export function validateRedirectUrl(url: string | null): string | null {
  if (!url) return null;

  // Must start with /
  if (!url.startsWith('/')) return null;

  // Block protocol-relative URLs (//evil.com)
  if (url.startsWith('//')) return null;

  // Block URLs with backslash (some browsers interpret \\ as //)
  if (url.includes('\\')) return null;

  // Block javascript: and other dangerous protocols (case-insensitive)
  const lowerUrl = url.toLowerCase();
  if (
    lowerUrl.includes('javascript:') ||
    lowerUrl.includes('data:') ||
    lowerUrl.includes('vbscript:')
  ) {
    return null;
  }

  // Decode and re-check to catch encoded bypass attempts
  try {
    const decoded = decodeURIComponent(url);
    if (decoded !== url) {
      // URL was encoded - validate the decoded version
      if (!decoded.startsWith('/') || decoded.startsWith('//')) {
        return null;
      }
      const lowerDecoded = decoded.toLowerCase();
      if (
        lowerDecoded.includes('javascript:') ||
        lowerDecoded.includes('data:') ||
        lowerDecoded.includes('vbscript:')
      ) {
        return null;
      }
    }
  } catch {
    // Invalid URL encoding - reject
    return null;
  }

  return url;
}

/**
 * Gets a validated redirect URL from a search param value.
 * Returns the default path if the URL is invalid or missing.
 *
 * @param redirectParam - The redirect URL from search params
 * @param defaultPath - The default path to use if redirect is invalid
 * @returns A safe redirect path
 */
export function getSafeRedirect(
  redirectParam: string | null,
  defaultPath: string = '/dashboard'
): string {
  const validated = validateRedirectUrl(redirectParam);
  return validated ?? defaultPath;
}
